import { motion } from 'motion/react';
import { Logo } from '@/shared/brand';
import { EASE } from '@/shared/motion/transitions';
import { HERO_IMG, STUDIO_CAPABILITIES } from '@/features/auth/data/marketing';
import { ExplorerLoginPromo } from './ExplorerLoginPromo';
import { StatBlock } from './StatBlock';

/**
 * Left "editorial" panel of the login screen — a warm greige liquid-glass hero
 * (matching the Vault design system) with brand, headline, and real product
 * capabilities. Hidden below the `lg` breakpoint.
 */
export function EditorialPanel() {
  return (
    <section className="hidden lg:flex lg:w-[58%] relative overflow-hidden flex-col text-ink-900">
      {/* warm decorative glow + faint brand watermark for depth over the greige gradient */}
      <div className="pointer-events-none absolute -top-24 -left-16 h-[420px] w-[420px] rounded-full bg-[radial-gradient(circle,rgba(214,180,116,0.28),transparent_70%)]" />
      <img
        src={HERO_IMG}
        alt=""
        aria-hidden
        className="pointer-events-none absolute -bottom-16 -right-10 w-[460px] max-w-none opacity-[0.06] mix-blend-multiply select-none"
        draggable={false}
      />

      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: EASE }}
        className="absolute top-9 left-10 right-10 z-10"
      >
        <Logo size="md" tone="light" showTagline />
      </motion.div>

      <div className="relative z-10 flex flex-col justify-between flex-1 w-full px-8 pt-28 pb-8 lg:px-10 lg:pt-32 lg:pb-10">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
          className="max-w-[560px]"
        >
          <p className="text-[11px] uppercase tracking-[0.26em] font-semibold text-gold-600 mb-5 flex items-center gap-3">
            <span className="h-px w-7 bg-gold-500/60" />
            Fine jewelry, in living 3D
          </p>
          <h1 className="font-headline text-[38px] xl:text-[46px] leading-[1.05] tracking-[-0.025em] text-ink-900">
            The 3D cloud for{' '}
            <span className="gold-gradient-text">fine&nbsp;jewelry</span> studios.
          </h1>
          <p className="mt-4 text-ink-600 text-[14px] leading-relaxed max-w-md">
            Upload your CAD, get photoreal diamonds and metals, then share interactive
            configurators, client lookbooks and 4K renders — all from one workspace.
          </p>

          <div className="mt-8 grid grid-cols-3 gap-4 max-w-lg">
            {STUDIO_CAPABILITIES.map((cap) => (
              <StatBlock key={cap.label} capability={cap} />
            ))}
          </div>
        </motion.div>

        <div className="max-w-[560px] shrink-0 space-y-3">
          <ExplorerLoginPromo variant="dark" />
          <p className="text-[11.5px] text-ink-500">
            Secure sign-in, workspace assets, and transparent plans once you enter the vault.
          </p>
        </div>
      </div>
    </section>
  );
}
